import React from "react";
import { Tooltip } from "@sscs/ui";
import { cssVars, variables } from "@sscs/ui/styles";
import type { Usage } from "./CapacityBar";
import styles from "./capacityBar.module.scss";

export type ChartData = (Usage & {
  formattedUsage?: string;
})[];

type BarChartProps = {
  data: ChartData;
  total: number;
  children: React.ReactNode;
};

const BarChart = ({
  data,
  total,
  children,
}: BarChartProps): React.JSX.Element => {
  const totalUsage = data.reduce((acc, usage) => acc + usage.used, 0);
  const remainingValue = total - totalUsage;
  const tooltipContent = (
    <div className={styles["tooltip-container"]}>
      {data.map((usage) => (
        <React.Fragment key={usage.name}>
          <div>{usage.name}:&nbsp;</div>
          <div>{usage.formattedUsage}</div>
        </React.Fragment>
      ))}
    </div>
  );

  // We won't display bar segments for 0 values
  const filtered = data.filter((usage) => usage.used > 0);

  // Create an additional bar if the totalUsage does not equal the total to
  // give the appearance of a background for the chart. Avoids complications
  // with having the bars clip to rounded corners.
  const chartData: ChartData = remainingValue
    ? [
        ...filtered,
        {
          name: "",
          used: remainingValue,
          color: cssVars.surface,
        },
      ]
    : filtered;

  return (
    <div className={styles["bar-chart"]}>
      <Tooltip
        trigger="graphic"
        title={tooltipContent}
        tooltipType="card"
        header="Usage"
      >
        <div className={styles["bars-wrapper"]}>
          {chartData.map((usage, index) => (
            <div
              key={usage.name}
              style={{
                flex: usage.used,
                backgroundColor: usage.color,
                borderRight:
                  index !== chartData.length - 1
                    ? `1px solid ${variables.text_medium}`
                    : "none",
              }}
            ></div>
          ))}
        </div>
      </Tooltip>
      {children}
    </div>
  );
};

export default BarChart;
